import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { GetApp } from "../../App"
import { useOverlay } from "../../App/useOverlay"
import { getUserAdvices, UserAdvice, UserAdviceLocation } from "../UserAdvice"
import { UserAdviceOverlayContent, UserAdviceOverlayView } from "../UserAdvice/OverlayContentView"
import { UserAdviceMonitorToggles } from "../../Components/UserAdvice/MonitorToggles"
import { UserAdviceRole } from "../../Components/UserAdvice/Params"
import { logger } from "../../App/Logger"
import { Icon, Icons } from "../../Components/Icon"
import { ILogLighting, ISailChange, IShipStatus } from "../../Types"
import { LogView } from "../LogView"
import { GetShipView } from "../ShipView"
import { ShipViewOverlayContent } from "../ShipOverlayContentView"
import { ShipInfoHeaderView } from "../ShipInfoHeaderView"
import { FocusViewConfirmationType, FocusViewData } from "./Context"
import { FocusContentView } from "./ContentView"
import { useFocusData } from "./DataContext"
import { FocusViewProps } from "./Params"
import { GetShip } from "../../App/Context"
import { useSettingsWriters } from "../../Data"

// The presentational half of FocusView. It reads the acquired current-day data (frame +
// today's logs/statuses + the live current log + the write surface) from `useFocusData()`
// -- populated by `FocusDataView` -- and owns the purely-derived render state the old
// monolith computed inline: today/yesterday/tomorrow day records, the user advices, the
// current-log entry view, the header actions and the confirmation-dialog + scroll UI. It
// never fetches or subscribes to data itself.
export function FocusViewUI({ show, initConfirmationType, initCruise, initTrip, initDay, initDayTab, initDayTomorrow, initDayTomorrowDetails, initLogs, initLogHighlight, initLogTabs, initLogButtonMenuOpen, initLogButtonSubmenu, initStatuses, initUserAdvice, initUserAdviceData }: FocusViewProps) {
  const app = GetApp()!;
  const ship = GetShip();
  const settingsWriters = useSettingsWriters(ship.id);
  const t = app.getT("FocusView");
  const { shipOverlay } = GetShipView();
  const { currentState, currentLog, editable, writers } = useFocusData();
  const [confirmationType, setConfirmationType] = useState<FocusViewConfirmationType | undefined>(initConfirmationType);
  const [focusViewScrolled, setFocusViewScrolled] = useState<boolean>(false);
  // `none` is the CLOSED state and so the reset target; `initUserAdvice` only seeds
  // which dialog starts open. Passing the seed as the default (as this did) made
  // closing restore it, so a dialog opened by a demo scene or a catalog fixture
  // could not be dismissed -- see `useOverlay`.
  const [userAdvice, _userAdviceOptions, setUserAdvice, resetUserAdvice] = useOverlay<string, undefined>(UserAdviceOverlayContent.none, initUserAdvice);
  const [userAdvices] = useState<UserAdvice[]>(() => getUserAdvices(app, settingsWriters));
  const currentLogViewRef = useRef<HTMLDivElement>(null);

  const navigationStarted = useMemo(() => app.status?.navigation == true, [app.status]);
  const currentCruise = useMemo(() => currentState?.cruise, [currentState]);
  const dayToday = useMemo(() => FocusViewData.dayToday(currentState, app.current?.log?.timezone), [currentState, app.current]);
  const dayTodayActive = useMemo(() => FocusViewData.dayTodayActive(dayToday, currentState?.logs || []), [dayToday, currentState?.logs]);
  const dayYesterday = useMemo(() => FocusViewData.dayYesterday(currentState), [currentState]);
  const dayTomorrow = useMemo(() => FocusViewData.dayTomorrow(currentState, dayTodayActive), [currentState, dayTodayActive]);

  // The user advices are imperative + carry native side effects (audible alarm, OS
  // notification, keep-alive) inside `update()`, so they cannot run in render. Update them
  // post-commit whenever the current-day state or the live current log changes -- the
  // same three moments the monolith drove `advice.update` (init, load, currentLog
  // broadcast), now unified since both inputs are React state/store-backed. The tick then
  // forces `getInstances` to re-read the freshly-updated advice state (replacing the old
  // `setN` that used to do this alongside the data merge).
  const [adviceTick, setAdviceTick] = useState(0);
  useEffect(() => {
    if (!currentState) {
      return;
    }
    for (const advice of userAdvices) {
      advice.update(app, currentState, currentLog, ship);
    }
    setAdviceTick((tick) => tick + 1);
  }, [app, app.status?.nmea?.liveAlerts, userAdvices, currentState, currentLog, ship]);

  // Gate each write on `editable` (and a present current state) exactly as the monolith's
  // record callbacks did, then delegate the concrete data update to the writer boundary
  // (the New variant's optimistic store `mutate`, or the Legacy variant's `updateData`
  // merge). The writers themselves stay ungated so a current/provisional log can never be
  // committed by a non-editor.
  const recordLog = useCallback(async (status?: IShipStatus) => {
    if (!currentState || !editable) {
      return;
    }
    await writers.recordLog(status);
  }, [currentState, editable, writers]);

  const recordEngine = useCallback(async (engineId: string, on: boolean) => {
    if (!currentState || !editable) {
      return;
    }
    logger.debug("engine", engineId, on);
    await writers.recordEngine(engineId, on);
  }, [currentState, editable, writers]);

  const recordLighting = useCallback(async (lighting: ILogLighting) => {
    if (!currentState || !editable) {
      return;
    }
    logger.debug("lighting", lighting);
    await writers.recordLighting(lighting);
  }, [currentState, editable, writers]);

  const recordSails = useCallback(async (changes: ISailChange[]) => {
    if (!currentState || !editable || changes.length == 0) {
      return;
    }
    logger.debug("sails", changes);
    await writers.recordSails(changes);
  }, [currentState, editable, writers]);

  // The advice buttons still speak in terms of a selected id; `setSelected(undefined)`
  // is the same "close me" the other hubs spell `resetOverlay()`.
  const userAdviceState = useMemo(() => {
    return {
      selected: userAdvice == UserAdviceOverlayContent.none ? undefined : userAdvice,
      setSelected: (selected?: string) => selected == undefined ? resetUserAdvice() : setUserAdvice(selected),
      data: initUserAdviceData,
      recordStop: writers.recordStop,
    };
  }, [userAdvice, setUserAdvice, resetUserAdvice, initUserAdviceData, writers.recordStop]);

  // Handed to LogView as instances rather than rendered nodes (#1171): the row picks a
  // display format per button from their severity, which it can only do knowing what
  // they are. Kept out of `currentLogView` so the overlay host can see them too.
  const logAdvices = useMemo(() => {
    if (!currentLog) {
      return [];
    }
    return userAdvices.flatMap((a) => a.getInstances(app, UserAdviceLocation.log, currentLog)).toSorted((a, b) => b.level - a.level);
  }, [currentLog, userAdvices, adviceTick]);

  const currentLogView = useMemo(() => {
    if (!currentLog || !currentState) {
      return undefined;
    }
    const statuses = currentState.statuses.filter((s) => dayToday && s.dayId == dayToday.id && s.endLogId == undefined);
    return <LogView advices={logAdvices} adviceState={userAdviceState} clearHighlight={() => {}} createCallback={recordLog} engineStatusCallback={recordEngine} initLogButtonMenuOpen={initLogButtonMenuOpen} initLogButtonSubmenu={initLogButtonSubmenu} lightingCallback={recordLighting} sailChangesCallback={recordSails} log={currentLog} statuses={statuses} ref={currentLogViewRef} />;
  }, [currentLog, currentState, dayToday, initLogButtonMenuOpen, initLogButtonSubmenu, userAdviceState, logAdvices, recordLog, recordEngine, recordLighting, recordSails]);

  const dayAdvices = useMemo(() => {
    return userAdvices.flatMap((a) => a.getInstances(app, UserAdviceLocation.day, currentLog)).toSorted((a, b) => a.level - b.level);
  }, [currentLog, userAdvices, adviceTick]);

  // The ship toolbar's monitors (#916). Filtered from the SAME advice objects the
  // effect above keeps updated -- `userAdvicesByRole()` would construct fresh ones
  // that `update()` never runs on, so every toggle would read as inactive forever.
  //
  // The log row and day summary above keep asking every advice, because each one
  // decides for itself where it appears; the monitors answer `toolbar` and nothing
  // else, so they have already left those two.
  const toolbarAdvices = useMemo(() => {
    return userAdvices
      .filter((a) => a.role == UserAdviceRole.monitor)
      .flatMap((a) => a.getInstances(app, UserAdviceLocation.toolbar, currentLog));
  }, [app, currentLog, userAdvices, adviceTick]);

  const monitorToggles = useCallback((collapsed: boolean) => {
    return <UserAdviceMonitorToggles advices={toolbarAdvices} app={app} collapsed={collapsed} state={userAdviceState} />;
  }, [app, toolbarAdvices, userAdviceState]);

  const dayUserAdvices = useMemo(() => {
    return dayAdvices.map((advice) => {
      return <React.Fragment key={advice.id}>{advice.renderButton(userAdviceState)}</React.Fragment>;
    });
  }, [dayAdvices, userAdviceState]);

  const actions = useMemo(() => {
    if (!currentState) {
      return [];
    }
    return [
      ...(app.editable ? [
        { disabled: false, id: "add-cruise", onClick: async () => {
          setConfirmationType(FocusViewConfirmationType.cruise);
          return true;
        }, title: <><Icon icon={Icons.Plus} /> {t("actions_add_cruise")}</> },
        { disabled: false, id: "add-trip", onClick: async () => {
          setConfirmationType(FocusViewConfirmationType.trip);
          return true;
        }, title: <><Icon icon={Icons.Plus} /> {t("actions_add_trip")}</> },
      ] : []),
    ];
  }, [currentState, app.language]);

  const completeConfirmation = useCallback((type?: FocusViewConfirmationType) => {
    logger.debug("completeConfirmation", type ? FocusViewConfirmationType[type] : "null");
    setConfirmationType(type);
  }, []);

  const previousDay = useMemo(() => {
    return FocusViewData.previousDayDefaults(dayToday);
  }, [dayToday?.endLocation, dayToday?.crew, dayToday?.passengers, dayToday?.skipper]);

  useEffect(() => {
    // `app.staticLayout` skips this real-browser scroll-into-view in the catalog +
    // unit-test (happy-dom) mock app so the golden snapshots stay deterministic
    // (see `IAppState.staticLayout`). Real users + E2E run it.
    if (app.staticLayout) return;
    if (currentLogViewRef.current && shipOverlay == ShipViewOverlayContent.none && show && !focusViewScrolled) {
      currentLogViewRef.current.style.top = "200px";
      setTimeout(() => {
        if (currentLogViewRef.current) {
          currentLogViewRef.current.scrollIntoView({ behavior: "instant", block: "end" });
        }
      }, 100);
      setFocusViewScrolled(true);
    } else if (!show) {
      setFocusViewScrolled(false);
    }
  }, [currentLogViewRef.current, currentLog, shipOverlay, show, focusViewScrolled]);

  if (!show) {
    return null;
  }

  return (
    <div className="FocusView">
      <UserAdviceOverlayView advices={[...logAdvices, ...dayAdvices, ...toolbarAdvices]} state={userAdviceState} />
      <ShipInfoHeaderView actions={actions} monitors={monitorToggles} />
      <FocusContentView
        confirmationType={confirmationType}
        completeConfirmation={completeConfirmation}
        shipOverlayActive={shipOverlay != ShipViewOverlayContent.none}
        currentState={currentState}
        currentCruise={currentCruise}
        currentLog={currentLog}
        currentLogView={currentLogView}
        dayUserAdvices={dayUserAdvices}
        navigationStarted={navigationStarted}
        dayYesterday={dayYesterday}
        dayToday={dayToday}
        dayTodayActive={dayTodayActive}
        dayTomorrow={dayTomorrow}
        previousDay={previousDay}
        initCruise={initCruise}
        initTrip={initTrip}
        initDay={initDay}
        initDayTab={initDayTab}
        initDayTomorrow={initDayTomorrow}
        initDayTomorrowDetails={initDayTomorrowDetails}
        initLogHighlight={initLogHighlight}
        initLogTabs={initLogTabs}
        initLogs={initLogs}
        initStatuses={initStatuses}
      />
    </div>
  );
}
